import { useState } from "react";
import { useStartJob, useJobs } from "../hooks/useJobs";
import { JobCard } from "../components/progress/JobCard";
import type {
  Category,
  GenerateRequest,
  PersonStyle,
  ReelTemplate,
} from "../api/types";

const CATEGORIES: { value: Category; label: string }[] = [
  { value: "auto", label: "Auto — Algorithmus wählt" },
  { value: "facts", label: "Facts — Datenpost" },
  { value: "general", label: "General — Brand-Message" },
  { value: "activation", label: "Activation — CTA + URL" },
  { value: "intro", label: "Intro — Tagline" },
  { value: "ending", label: "Ending — Abschluss" },
  { value: "person", label: "Person — KI-Porträt" },
  { value: "reel", label: "Reel — Hochformat-Video" },
  { value: "carousel", label: "Carousel — Multi-Slide" },
];

const REEL_TEMPLATES: { value: ReelTemplate; label: string }[] = [
  { value: "fact_punch", label: "Fact Punch · 9s, nur Typografie" },
  { value: "myth_vs_fact", label: "Mythos vs Fakt · 15s, narriert" },
  { value: "claim_crack", label: "Claim Crack · 12s, mit B-Roll" },
  { value: "testimonial_reel", label: "Testimonial · 15s, Porträt" },
  { value: "breaking_pattern", label: "Breaking Pattern · 9s, rapid-fire" },
];

const PERSON_STYLES: { value: PersonStyle; label: string }[] = [
  { value: "phone", label: "Phone · Mit TRULY-App" },
  { value: "everyday", label: "Everyday · Café / Park" },
  { value: "testimonial", label: "Testimonial · Porträt" },
  { value: "group", label: "Group · Straßenszene" },
];

export function Generate() {
  const [category, setCategory] = useState<Category>("reel");
  const [template, setTemplate] = useState<ReelTemplate>("fact_punch");
  const [style, setStyle] = useState<PersonStyle>("everyday");
  const [slides, setSlides] = useState<number>(5);
  const [asCarousel, setAsCarousel] = useState(false);
  const [topic, setTopic] = useState("");
  const [dryRun, setDryRun] = useState(true);

  const startJob = useStartJob();
  const { data: jobs = [], isLoading: jobsLoading } = useJobs();

  const carouselToggleAvailable =
    category !== "reel" && category !== "auto" && category !== "carousel";

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const req: GenerateRequest = {
      category,
      topic_override: topic.trim() || undefined,
      dry_run: dryRun,
    };
    if (category === "reel") req.template = template;
    if (category === "person") req.style = style;
    if (category === "carousel") req.slides = slides;
    if (carouselToggleAvailable && asCarousel) {
      req.as_carousel = true;
      req.slides = slides;
    }
    // Fire and forget — the job list below picks it up via polling.
    startJob.mutate(req);
    // Clear the topic so the user can type another one immediately.
    setTopic("");
  };

  const running = jobs.filter((j) => j.is_running);
  const finished = jobs.filter((j) => !j.is_running);

  return (
    <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
      {/* ── Form column ── */}
      <div className="xl:col-span-2">
        <h1 className="headline text-6xl text-brand-text-dark">Generieren</h1>
        <p className="mt-2 text-sm text-brand-text-dark/60">
          Feuert den Pipeline-Lauf. Du kannst <em>mehrere</em> Jobs gleichzeitig
          laufen lassen — jeder erscheint rechts mit eigenem Live-Fortschritt.
          Wenn du wegnavigierst, bleibt der Job auf dem Server und zeigt sich
          bei Rückkehr wieder.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <Field label="Kategorie">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
              className="input-base"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>

          {category === "reel" ? (
            <Field label="Reel-Template">
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value as ReelTemplate)}
                className="input-base"
              >
                {REEL_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {category === "person" ? (
            <Field label="Person-Style">
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as PersonStyle)}
                className="input-base"
              >
                {PERSON_STYLES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {category === "carousel" ? (
            <Field label="Slides">
              <input
                type="number"
                min={3}
                max={10}
                value={slides}
                onChange={(e) => setSlides(Number(e.target.value))}
                className="input-base"
              />
            </Field>
          ) : null}

          {carouselToggleAvailable ? (
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={asCarousel}
                  onChange={(e) => setAsCarousel(e.target.checked)}
                  className="mt-1 h-4 w-4"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-brand-text-dark">
                    Als Carousel rendern
                  </div>
                  <div className="text-xs text-brand-text-dark/60">
                    Mehrere Slides statt Einzelpost. Narrativer Bogen wird
                    auf <code className="rounded bg-black/5 px-1">{category}</code> getrimmt.
                  </div>
                </div>
              </label>
              {asCarousel ? (
                <div className="mt-3 flex items-center gap-2 border-t border-black/5 pt-3">
                  <label className="text-xs font-bold uppercase tracking-wider text-brand-text-dark/70">
                    Slides
                  </label>
                  <input
                    type="number"
                    min={3}
                    max={10}
                    value={slides}
                    onChange={(e) => setSlides(Number(e.target.value))}
                    className="w-20 rounded-md border border-black/10 bg-white px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-brand-text-dark/50">(3–10)</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <Field label="Topic override (optional)">
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="z.B. Kerosin-Knappheit und Hormus"
              className="input-base"
            />
          </Field>

          <label className="flex cursor-pointer items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-black/5">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="h-4 w-4"
            />
            <div>
              <div className="text-sm font-bold text-brand-text-dark">
                Dry-Run
              </div>
              <div className="text-xs text-brand-text-dark/60">
                Notion-Draft überspringen, nur lokal speichern
              </div>
            </div>
          </label>

          <button
            type="submit"
            disabled={startJob.isPending}
            className="w-full rounded-full bg-brand-blue px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-brand-cream shadow-lg shadow-brand-blue/30 transition hover:bg-brand-blue-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {startJob.isPending ? "Starte…" : "Generate"}
          </button>
          {startJob.error ? (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-900">
              {String(startJob.error)}
            </div>
          ) : null}
        </form>
      </div>

      {/* ── Jobs column ── */}
      <div className="xl:col-span-3">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/50">
            Live jobs
          </h2>
          <div className="text-xs text-brand-text-dark/50">
            {running.length} running · {finished.length} done
          </div>
        </div>

        {jobsLoading ? (
          <div className="rounded-xl border-2 border-dashed border-brand-text-dark/15 bg-white/40 p-6 text-sm text-brand-text-dark/60">
            Lade Jobs…
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-brand-text-dark/15 bg-white/40 p-6 text-sm text-brand-text-dark/60">
            Noch keine Jobs. Trag links was ein und drück Generate.
          </div>
        ) : (
          <div className="space-y-3">
            {running.map((j) => (
              <JobCard key={j.job_id} job={j} />
            ))}
            {finished.length > 0 && running.length > 0 ? (
              <div className="pt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-text-dark/40">
                Finished
              </div>
            ) : null}
            {finished.map((j) => (
              <JobCard key={j.job_id} job={j} />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .input-base {
          display: block;
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(0 0 0 / 0.1);
          background: white;
          padding: 0.625rem 0.875rem;
          font-size: 0.875rem;
          color: var(--color-brand-text-dark);
          transition: border-color 120ms;
        }
        .input-base:focus {
          border-color: var(--color-brand-blue);
          outline: none;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand-text-dark/70">
        {label}
      </div>
      {children}
    </label>
  );
}
